import { useEffect } from 'react';
import { matchRoutes, RouteObject, useLocation } from 'react-router-dom';
import { useDispatch } from 'store';
import { declineFocusedEdit } from 'store/reducers/menu';
import { MenuProps } from 'types/menu';

/** A route that renders a focused-edit form names the key its form passes to `useFocusedEditMode`. */
type FocusedEditRoute = RouteObject & { focusedEdit?: string };

/** The key of the focused-edit form the route at `pathname` renders, if it renders one. */
export const focusedEditKey = (routes: RouteObject[], pathname: string): string | undefined =>
    (matchRoutes(routes, pathname)?.at(-1)?.route as FocusedEditRoute | undefined)?.focusedEdit;

/**
 * The layout's focused-edit state for this render.
 *
 * A route without the key keeps the chrome whatever the store says: after a navigation away from a
 * form, the store holds that form's mode until its unmount cleanup runs, which is after the new page
 * has rendered. On a route with it, the edit form turns the mode on in an effect once it has mounted,
 * which is after the layout and the page's loader have painted. Until it has, the route stands in for
 * it, with the page's saved fullscreen preference, so the nav is gone from the first frame instead of
 * leaving once the form arrives. Once the form has turned the mode on, the store decides: it carries
 * the user's toggle. A route whose page has `declined` (it settled without its form) has no stand-in.
 * Nor has one whose form `opensInCreateDialog`: the route only redirects, to a page that keeps its chrome.
 */
export function resolveFocusedEdit(
    routes: RouteObject[],
    pathname: string,
    menu: Pick<MenuProps, 'focusedEditMode' | 'fullscreenMode'>,
    savedFullscreen: (pageKey: string) => boolean | undefined,
    declined = false,
    opensInCreateDialog = false
): { focusedEditMode: boolean; isFullscreen: boolean } {
    const pageKey = focusedEditKey(routes, pathname);
    if (pageKey === undefined || opensInCreateDialog) {
        return { focusedEditMode: false, isFullscreen: false };
    }
    if (menu.focusedEditMode || declined) {
        return { focusedEditMode: menu.focusedEditMode, isFullscreen: menu.focusedEditMode && menu.fullscreenMode };
    }
    return { focusedEditMode: true, isFullscreen: savedFullscreen(pageKey) ?? menu.fullscreenMode };
}

/**
 * Rendered by a focused-edit route in place of a form that is not coming (its load failed), so the
 * layout gives the page its nav and header back instead of leaving it on an empty, chromeless screen.
 */
export function FocusedEditDeclined(): null {
    const dispatch = useDispatch();
    const { key } = useLocation();
    useEffect(() => {
        dispatch(declineFocusedEdit(key));
    }, [dispatch, key]);
    return null;
}
